import { getSalonSlug, getSalonBaseDomain } from "../utils/salonHost"

const CLIENT_TOKEN_KEY = "jwt-token"
const CLIENT_EXPIRES_KEY = "jwt-expires"
const CLIENT_ID_KEY = "client-id"
const CLIENT_PHONE_KEY = "client-phone"
const USER_TOKEN_KEY = "user-jwt-token"
const USER_EXPIRES_KEY = "user-jwt-expires"
const USER_ID_KEY = "user-id"
const USER_PHONE_KEY = "user-phone"
const USER_PROFILE_KEY = "user-profile"
const CLIENT_PROFILE_KEY = "client-profile"
const LANGUAGE_KEY = "language"
const DISPLAYED_USER_IDS_KEY = "displayed-user-ids"
const ADMIN_COMPANY_ID_KEY = "admin:companyId"

const LOCAL_DEV_HOSTS = new Set(["localhost", "127.0.0.1"])

const USER_AUTH_COOKIE_KEYS = [
  USER_ID_KEY,
  USER_TOKEN_KEY,
  USER_EXPIRES_KEY,
  USER_PHONE_KEY,
  USER_PROFILE_KEY,
]

function getAuthCookieDomain() {
  if (typeof window === "undefined") return null
  const hostname = window.location.hostname
  if (LOCAL_DEV_HOSTS.has(hostname)) {
    if (hostname.endsWith(".localhost")) return ".localhost"
    return null
  }
  return `.${getSalonBaseDomain()}`
}

function setAuthCookie(name, value, expiresMs) {
  if (typeof document === "undefined") return
  const expires = new Date(Number(expiresMs)).toUTCString()
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
  const domain = getAuthCookieDomain()
  if (domain) cookie += `; domain=${domain}`
  if (window.location.protocol === "https:") cookie += "; Secure"
  document.cookie = cookie
}

function getAuthCookie(name) {
  if (typeof document === "undefined") return null
  const target = `${encodeURIComponent(name)}=`
  const parts = document.cookie.split("; ")
  for (const part of parts) {
    if (part.startsWith(target)) {
      return decodeURIComponent(part.slice(target.length))
    }
  }
  return null
}

function removeAuthCookie(name) {
  if (typeof document === "undefined") return
  let cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`
  const domain = getAuthCookieDomain()
  if (domain) cookie += `; domain=${domain}`
  if (window.location.protocol === "https:") cookie += "; Secure"
  document.cookie = cookie
}

function readUserItem(key) {
  const cookieValue = getAuthCookie(key)
  if (cookieValue != null && cookieValue !== "") return cookieValue
  return localStorage.getItem(key)
}

function writeUserItem(key, value, expiresMs) {
  localStorage.setItem(key, value)
  if (expiresMs != null) {
    setAuthCookie(key, value, expiresMs)
  }
}

function removeUserItem(key) {
  localStorage.removeItem(key)
  removeAuthCookie(key)
}

function syncUserAuthFromLocalStorage() {
  const expires = localStorage.getItem(USER_EXPIRES_KEY)
  if (!expires || Date.now() >= Number(expires)) return
  for (const key of USER_AUTH_COOKIE_KEYS) {
    const value = localStorage.getItem(key)
    if (value != null && value !== "" && !getAuthCookie(key)) {
      setAuthCookie(key, value, expires)
    }
  }
}

if (typeof window !== "undefined") {
  syncUserAuthFromLocalStorage()
}

function scopedClientKey(baseKey) {
  const slug = getSalonSlug()
  return slug ? `company:${slug}:${baseKey}` : baseKey
}

function readClientItem(baseKey) {
  return localStorage.getItem(scopedClientKey(baseKey))
}

function writeClientItem(baseKey, value) {
  localStorage.setItem(scopedClientKey(baseKey), value)
}

function removeClientItem(baseKey) {
  localStorage.removeItem(scopedClientKey(baseKey))
}

export function setClientTokens({
  clientId,
  clientPhone,
  clientToken,
  clientExpiresIn = 60 * 60 * 24 * 30,
}) {
  const expiresDate = new Date().getTime() + clientExpiresIn * 1000
  writeClientItem(CLIENT_ID_KEY, clientId)
  writeClientItem(CLIENT_TOKEN_KEY, clientToken)
  writeClientItem(CLIENT_EXPIRES_KEY, expiresDate)
  writeClientItem(CLIENT_PHONE_KEY, clientPhone)
}

export function setUserTokens({
  userId,
  userPhone,
  userToken,
  userExpiresIn = 60 * 60 * 24 * 30,
}) {
  const expiresDate = new Date().getTime() + userExpiresIn * 1000
  writeUserItem(USER_ID_KEY, userId, expiresDate)
  writeUserItem(USER_TOKEN_KEY, userToken, expiresDate)
  writeUserItem(USER_EXPIRES_KEY, expiresDate, expiresDate)
  writeUserItem(USER_PHONE_KEY, userPhone, expiresDate)
}

export function removeClientAuthData() {
  removeClientItem(CLIENT_ID_KEY)
  removeClientItem(CLIENT_TOKEN_KEY)
  removeClientItem(CLIENT_EXPIRES_KEY)
  removeClientItem(CLIENT_PHONE_KEY)
  removeClientItem(CLIENT_PROFILE_KEY)
}

export function removeUserAuthData() {
  for (const key of USER_AUTH_COOKIE_KEYS) {
    removeUserItem(key)
  }
}

export function getClientAccessToken() {
  return readClientItem(CLIENT_TOKEN_KEY)
}

export function getUserAccessToken() {
  return readUserItem(USER_TOKEN_KEY)
}

export function getClientExpiresDate() {
  return readClientItem(CLIENT_EXPIRES_KEY)
}

export function getUserExpiresDate() {
  return readUserItem(USER_EXPIRES_KEY)
}

export function getClientId() {
  return readClientItem(CLIENT_ID_KEY)
}

export function getUserId() {
  return readUserItem(USER_ID_KEY)
}

export function getClientPhone() {
  return readClientItem(CLIENT_PHONE_KEY)
}

export function getUserPhone() {
  return readUserItem(USER_PHONE_KEY)
}

export function setClientPhone(clientPhone) {
  writeClientItem(CLIENT_PHONE_KEY, clientPhone)
}

export function setUserPhone(userPhone) {
  const expires = getUserExpiresDate()
  if (expires) {
    writeUserItem(USER_PHONE_KEY, userPhone, expires)
    return
  }
  localStorage.setItem(USER_PHONE_KEY, userPhone)
}

export function getLanguage() {
  return localStorage.getItem(LANGUAGE_KEY)
}

export function setLanguage(language) {
  localStorage.setItem(LANGUAGE_KEY, language)
}

export function setUserProfile(profile) {
  const expires = getUserExpiresDate()
  if (!profile) {
    removeUserItem(USER_PROFILE_KEY)
    return
  }
  const serialized = JSON.stringify(profile)
  if (expires) {
    writeUserItem(USER_PROFILE_KEY, serialized, expires)
    return
  }
  localStorage.setItem(USER_PROFILE_KEY, serialized)
}

export function getUserProfile() {
  const raw = readUserItem(USER_PROFILE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    removeUserItem(USER_PROFILE_KEY)
    return null
  }
}

export function setClientProfile(profile) {
  if (!profile) {
    removeClientItem(CLIENT_PROFILE_KEY)
    return
  }
  writeClientItem(CLIENT_PROFILE_KEY, JSON.stringify(profile))
}

export function isAdminSessionValid() {
  const token = getUserAccessToken()
  const expires = getUserExpiresDate()
  if (!token || !expires) return false
  if (Date.now() >= Number(expires)) return false
  const profile = getUserProfile()
  if (!profile?.isCompanyOwner && !profile?.isPlatformAdmin) return false
  if (!profile.isPlatformAdmin && profile.companyActive === false) return false
  return true
}

export function isUserSessionValid() {
  const token = getUserAccessToken()
  const expires = getUserExpiresDate()
  if (!token || !expires) return false
  return Date.now() < Number(expires)
}

export function isClientSessionValid() {
  const token = getClientAccessToken()
  const expires = getClientExpiresDate()
  if (!token || !expires) return false
  return Date.now() < Number(expires)
}

export function getClientProfile() {
  const raw = readClientItem(CLIENT_PROFILE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    removeClientItem(CLIENT_PROFILE_KEY)
    return null
  }
}

export function setDisplayedUserIds(ids) {
  localStorage.setItem(DISPLAYED_USER_IDS_KEY, JSON.stringify(ids))
}

export function getAdminCompanyId() {
  const raw = localStorage.getItem(ADMIN_COMPANY_ID_KEY)
  if (!raw) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

export function setAdminCompanyId(companyId) {
  if (companyId == null) {
    localStorage.removeItem(ADMIN_COMPANY_ID_KEY)
    return
  }
  localStorage.setItem(ADMIN_COMPANY_ID_KEY, String(companyId))
}

export function resolveScopedCompanyId(profile) {
  if (!profile) return null
  if (profile.isPlatformAdmin) {
    return getAdminCompanyId() ?? profile.companyId ?? null
  }
  return profile.companyId ?? null
}

export function getDisplayedUserIds() {
  const raw = localStorage.getItem(DISPLAYED_USER_IDS_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : []
  } catch {
    localStorage.removeItem(DISPLAYED_USER_IDS_KEY)
    return []
  }
}

const localStorageService = {
  setClientTokens,
  setUserTokens,
  removeClientAuthData,
  removeUserAuthData,
  getClientAccessToken,
  getUserAccessToken,
  getClientId,
  getUserId,
  getClientExpiresDate,
  getUserExpiresDate,
  getClientPhone,
  getUserPhone,
  setClientPhone,
  setUserPhone,
  getLanguage,
  setLanguage,
  setUserProfile,
  getUserProfile,
  setClientProfile,
  getClientProfile,
  isAdminSessionValid,
  isUserSessionValid,
  isClientSessionValid,
  setDisplayedUserIds,
  getDisplayedUserIds,
  getAdminCompanyId,
  setAdminCompanyId,
  resolveScopedCompanyId,
}

export default localStorageService
